import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import ReactECharts from "echarts-for-react";
import "leaflet/dist/leaflet.css";
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  BarChart3,
  Activity,
  AlertTriangle,
  Leaf,
  Target,
  ArrowUpRight,
  MapPin
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";

// Custom gold marker icon
const goldIcon = new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="width: 20px; height: 20px; background: #c9a227; border-radius: 50%; border: 3px solid white; box-shadow: 0 0 15px rgba(201, 162, 39, 0.6);"></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
  popupAnchor: [0, -10]
});

const KPICard = ({ title, value, icon: Icon, trend, trendValue, format = "number" }) => {
  const formatValue = (val) => {
    if (format === "currency") {
      if (val >= 1000000000) return `$${(val / 1000000000).toFixed(2)}B`;
      if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
      return `$${val.toLocaleString()}`;
    }
    if (format === "percentage") return `${val}%`;
    return val.toLocaleString();
  };

  return (
    <Card className="glass-card" data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm text-slate-400 uppercase tracking-wider">{title}</p>
            <p className="text-3xl font-bold text-[#c9a227] mt-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {formatValue(value)}
            </p>
            {trendValue !== undefined && (
              <div className={`flex items-center gap-1 mt-2 text-sm ${trend === "up" ? "text-green-400" : "text-red-400"}`}>
                {trend === "up" ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                <span>{trendValue}%</span>
              </div>
            )}
          </div>
          <div className="w-12 h-12 rounded-sm bg-[#c9a227]/10 flex items-center justify-center">
            <Icon className="w-6 h-6 text-[#c9a227]" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const ExecutiveDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [regions, setRegions] = useState([]);
  const [portfolioTrend, setPortfolioTrend] = useState([]);
  const [roiTrend, setRoiTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        
        // Seed projects and regions
        await axios.post(`${API}/seed-projects`, {}, { headers });
        await axios.post(`${API}/seed-regions`, {}, { headers });
        
        // Fetch map data with projects and regions
        const mapDataRes = await axios.get(`${API}/projects/map-data`, { headers });
        setProjects(mapDataRes.data.projects || []);
        setRegions(mapDataRes.data.regions || []);
        
        // Calculate KPIs from projects
        const projectList = mapDataRes.data.projects || [];
        const totalBudget = projectList.reduce((acc, p) => acc + (p.budget_total || 0), 0);
        const totalSpent = projectList.reduce((acc, p) => acc + (p.budget_spent || 0), 0);
        const activeProjects = projectList.filter(p => p.status === "active").length;
        
        setDashboardData({
          kpis: {
            total_portfolio_value: totalBudget,
            capital_deployed: totalSpent,
            roi: totalSpent > 0 ? Math.round((totalBudget - totalSpent) / totalSpent * 100) : 0,
            active_projects: activeProjects
          },
          projects: projectList
        });
        
        // Generate trend data
        const months = ['Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        setPortfolioTrend(months.map((m, i) => ({ month: m, value: totalBudget * (0.7 + i * 0.06) })));
        setRoiTrend(months.map((m, i) => ({ month: m, roi: 8 + i * 2 + Math.random() * 3 })));
        
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const portfolioChartOptions = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(15, 23, 42, 0.95)',
      borderColor: 'rgba(201, 162, 39, 0.3)',
      textStyle: { color: '#f8fafc' }
    },
    grid: { left: '3%', right: '4%', bottom: '3%', top: '10%', containLabel: true },
    xAxis: {
      type: 'category',
      data: portfolioTrend.map(d => d.month),
      axisLine: { lineStyle: { color: '#334155' } },
      axisLabel: { color: '#94a3b8' }
    },
    yAxis: {
      type: 'value',
      axisLine: { show: false },
      splitLine: { lineStyle: { color: '#1e293b' } },
      axisLabel: { 
        color: '#94a3b8',
        formatter: (val) => `$${(val / 1000000000).toFixed(1)}B`
      }
    },
    series: [{
      data: portfolioTrend.map(d => d.value),
      type: 'line',
      smooth: true,
      lineStyle: { color: '#c9a227', width: 3 },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(201, 162, 39, 0.3)' },
            { offset: 1, color: 'rgba(201, 162, 39, 0)' }
          ]
        }
      },
      itemStyle: { color: '#c9a227' }
    }]
  };

  const roiChartOptions = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(15, 23, 42, 0.95)',
      borderColor: 'rgba(201, 162, 39, 0.3)',
      textStyle: { color: '#f8fafc' }
    },
    grid: { left: '3%', right: '4%', bottom: '3%', top: '10%', containLabel: true },
    xAxis: {
      type: 'category',
      data: roiTrend.map(d => d.month),
      axisLine: { lineStyle: { color: '#334155' } },
      axisLabel: { color: '#94a3b8' }
    },
    yAxis: {
      type: 'value',
      axisLine: { show: false },
      splitLine: { lineStyle: { color: '#1e293b' } },
      axisLabel: { color: '#94a3b8', formatter: '{value}%' }
    },
    series: [{
      data: roiTrend.map(d => d.roi),
      type: 'bar',
      barWidth: '60%',
      itemStyle: {
        color: {
          type: 'linear',
          x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: '#c9a227' },
            { offset: 1, color: 'rgba(201, 162, 39, 0.3)' }
          ]
        },
        borderRadius: [4, 4, 0, 0]
      }
    }]
  };

  const sectorChartOptions = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'item',
      backgroundColor: 'rgba(15, 23, 42, 0.95)',
      borderColor: 'rgba(201, 162, 39, 0.3)',
      textStyle: { color: '#f8fafc' },
      formatter: '{b}: ${c}M ({d}%)'
    },
    legend: {
      orient: 'vertical',
      right: '5%',
      top: 'center',
      textStyle: { color: '#94a3b8' }
    },
    series: [{
      type: 'pie',
      radius: ['40%', '70%'],
      center: ['35%', '50%'],
      avoidLabelOverlap: false,
      label: { show: false },
      emphasis: {
        label: { show: true, fontSize: 14, fontWeight: 'bold', color: '#f8fafc' }
      },
      labelLine: { show: false },
      data: projects?.length > 0 ? 
        Object.entries(
          projects.reduce((acc, p) => {
            const sector = p.sector || 'Other';
            acc[sector] = (acc[sector] || 0) + (p.budget_total || 0) / 1000000;
            return acc;
          }, {})
        ).map(([name, value]) => ({ name, value: Math.round(value) })) 
        : [],
      itemStyle: {
        borderRadius: 4,
        borderColor: '#0b1c2d',
        borderWidth: 2
      },
      color: ['#c9a227', '#22c55e', '#3b82f6', '#ef4444', '#a855f7', '#f97316', '#06b6d4', '#ec4899']
    }]
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-[#c9a227] text-xl">Loading dashboard...</div>
      </div>
    );
  }

  const { kpis } = dashboardData || {};

  return (
    <div className="space-y-6" data-testid="executive-dashboard">
      {/* Hero Section with Map */}
      <div className="hero-section rounded-sm overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Map */}
          <div className="lg:col-span-2 h-[400px] rounded-sm overflow-hidden relative">
            <MapContainer
              center={[5, 20]}
              zoom={3}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={false}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
              />
              {/* Region Heat Markers */}
              {regions?.map((region) => region.lat && region.lng && (
                <Marker
                  key={`region-${region.id}`}
                  position={[region.lat, region.lng]}
                  icon={new L.DivIcon({
                    className: 'region-marker',
                    html: `<div style="width: ${Math.max(30, region.project_count * 15)}px; height: ${Math.max(30, region.project_count * 15)}px; background: radial-gradient(circle, rgba(201, 162, 39, 0.8) 0%, rgba(201, 162, 39, 0.2) 70%, transparent 100%); border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; font-size: 12px;">${region.project_count > 0 ? region.project_count : ''}</div>`,
                    iconSize: [Math.max(30, region.project_count * 15), Math.max(30, region.project_count * 15)],
                    iconAnchor: [Math.max(15, region.project_count * 7.5), Math.max(15, region.project_count * 7.5)],
                  })}
                >
                  <Popup>
                    <div className="min-w-[220px] p-2" style={{ color: '#1a1a1a' }}>
                      <h3 className="font-bold text-lg" style={{ color: '#b8922a' }}>{region.name}</h3>
                      <p className="text-xs mt-1" style={{ color: '#666' }}>{region.code}</p>
                      <div className="mt-2 space-y-1">
                        <p className="text-sm" style={{ color: '#1a1a1a' }}>Projects: <span style={{ color: '#b8922a', fontWeight: 'bold' }}>{region.project_count}</span></p>
                        <p className="text-sm" style={{ color: '#1a1a1a' }}>Active: <span style={{ color: '#22c55e', fontWeight: 'bold' }}>{region.active_projects}</span></p>
                        <p className="text-sm" style={{ color: '#1a1a1a' }}>Budget: <span style={{ color: '#b8922a', fontWeight: 'bold' }}>${((region.total_budget || 0) / 1000000).toFixed(0)}M</span></p>
                      </div>
                      <p className="text-xs mt-2" style={{ color: '#666' }}>Countries: {region.countries_list?.slice(0, 3).join(', ')}{region.countries_list?.length > 3 ? '...' : ''}</p>
                    </div>
                  </Popup>
                </Marker>
              ))}
              {/* Project Markers */}
              {projects?.map((project) => project.lat && project.lng && (
                <Marker
                  key={project.id}
                  position={[project.lat, project.lng]}
                  icon={goldIcon}
                  eventHandlers={{
                    click: () => navigate(`/projects/${project.id}`)
                  }}
                >
                  <Popup>
                    <div className="min-w-[200px] p-2" style={{ color: '#1a1a1a' }}>
                      <h3 className="font-bold text-lg" style={{ color: '#b8922a' }}>{project.name}</h3>
                      <p className="text-sm mt-1" style={{ color: '#666' }}>{project.country}</p>
                      <div className="mt-2 space-y-1">
                        <p className="text-sm" style={{ color: '#1a1a1a' }}>Budget: <span style={{ color: '#b8922a', fontWeight: 'bold' }}>${((project.budget_total || 0) / 1000000).toFixed(0)}M</span></p>
                        <p className="text-sm" style={{ color: '#1a1a1a' }}>Industry: <span style={{ color: '#3b82f6', fontWeight: 'bold' }}>{project.industry || project.sector}</span></p>
                        <p className="text-sm" style={{ color: '#1a1a1a' }}>Completion: <span style={{ color: '#22c55e', fontWeight: 'bold' }}>{project.completion_percentage || 0}%</span></p>
                      </div>
                      <button 
                        onClick={() => navigate(`/projects/${project.id}`)}
                        className="mt-3 text-xs flex items-center gap-1"
                        style={{ color: '#b8922a', textDecoration: 'underline' }}
                      >
                        View Details <ArrowUpRight className="w-3 h-3" />
                      </button>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
            
            {/* Map Overlay Title */}
            <div className="absolute top-4 left-4 z-[1000]">
              <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Africa Portfolio Overview
              </h2>
              <p className="text-slate-400 text-sm mt-1">
                {projects?.length || 0} Active Projects Across {regions?.length || 0} Regions
              </p>
            </div>
            
            {/* Map Legend */}
            <div className="absolute bottom-4 left-4 z-[1000] bg-slate-900/90 backdrop-blur-sm rounded-sm p-3 border border-slate-700">
              <p className="text-xs text-slate-400 font-semibold mb-2">LEGEND</p>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full" style={{ background: 'radial-gradient(circle, rgba(201, 162, 39, 0.8) 0%, rgba(201, 162, 39, 0.3) 100%)' }}></div>
                  <span className="text-xs text-slate-300">Region Heat (size = project count)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-[#c9a227]"></div>
                  <span className="text-xs text-slate-300">Individual Project Location</span>
                </div>
                <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-700">
                  <div className="w-3 h-3 rounded-full bg-[#c9a227]/30"></div>
                  <span className="text-xs text-slate-400">1-2 Projects</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-[#c9a227]/50"></div>
                  <span className="text-xs text-slate-400">3-4 Projects</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-[#c9a227]/70"></div>
                  <span className="text-xs text-slate-400">5+ Projects</span>
                </div>
              </div>
            </div>
          </div>

          {/* Side KPIs */}
          <div className="space-y-4">
            <Card className="glass-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-slate-300">Portfolio Health</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
                    <span className="text-2xl font-bold text-green-400">85</span>
                  </div>
                  <div>
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Healthy</Badge>
                    <p className="text-sm text-slate-400 mt-1">All systems operational</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-slate-300">Risk Alerts</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-3 p-2 bg-yellow-500/10 rounded-sm border border-yellow-500/20">
                  <AlertTriangle className="w-5 h-5 text-yellow-400" />
                  <div>
                    <p className="text-sm text-yellow-400">Currency Volatility</p>
                    <p className="text-xs text-slate-500">2 projects affected</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-2 bg-blue-500/10 rounded-sm border border-blue-500/20">
                  <Activity className="w-5 h-5 text-blue-400" />
                  <div>
                    <p className="text-sm text-blue-400">Regulatory Update</p>
                    <p className="text-xs text-slate-500">Kenya mining sector</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-slate-300">ESG Score</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Leaf className="w-5 h-5 text-green-400" />
                    <span className="text-slate-400">Environmental</span>
                  </div>
                  <span className="text-green-400 font-semibold">{kpis?.esg_score || 83}</span>
                </div>
                <Progress value={kpis?.esg_score || 83} className="h-2 mt-2 bg-slate-700" />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Total Portfolio Value"
          value={kpis?.total_portfolio_value || 0}
          icon={DollarSign}
          format="currency"
          trend="up"
          trendValue={12.5}
        />
        <KPICard
          title="Capital Deployed"
          value={kpis?.capital_deployed || 0}
          icon={Target}
          format="currency"
          trend="up"
          trendValue={8.3}
        />
        <KPICard
          title="ROI"
          value={kpis?.roi || 0}
          icon={TrendingUp}
          format="percentage"
          trend="up"
          trendValue={2.1}
        />
        <KPICard
          title="Active Projects"
          value={kpis?.active_projects || 0}
          icon={BarChart3}
          format="number"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="text-slate-200">Portfolio Value Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <ReactECharts option={portfolioChartOptions} style={{ height: '300px' }} />
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="text-slate-200">ROI Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <ReactECharts option={roiChartOptions} style={{ height: '300px' }} />
          </CardContent>
        </Card>
      </div>

      {/* Sector Distribution & Projects Table */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="text-slate-200">Capital by Sector</CardTitle>
          </CardHeader>
          <CardContent>
            <ReactECharts option={sectorChartOptions} style={{ height: '300px' }} />
          </CardContent>
        </Card>

        <Card className="glass-card lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-slate-200">Active Projects</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Project</th>
                    <th>Region</th>
                    <th>Budget</th>
                    <th>ROI</th>
                    <th>Progress</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {projects?.slice(0, 5).map((project) => (
                    <tr 
                      key={project.id} 
                      className="cursor-pointer"
                      onClick={() => navigate(`/projects/${project.id}`)}
                      data-testid={`project-row-${project.id}`}
                    >
                      <td>
                        <div className="flex items-center gap-3">
                          <MapPin className="w-4 h-4 text-[#c9a227]" />
                          <div>
                            <p className="font-medium text-white">{project.name?.length > 25 ? project.name.slice(0, 25) + '...' : project.name}</p>
                            <p className="text-xs text-slate-500">{project.country}</p>
                          </div>
                        </div>
                      </td>
                      <td className="text-slate-400">{project.region?.length > 20 ? project.region.slice(0, 20) + '...' : project.region}</td>
                      <td className="text-[#c9a227] font-semibold">${((project.budget_total || 0) / 1000000).toFixed(0)}M</td>
                      <td className="text-green-400">{project.completion_percentage || 0}%</td>
                      <td>
                        <div className="flex items-center gap-2">
                          <Progress value={project.completion_percentage || 0} className="h-2 w-20 bg-slate-700" />
                          <span className="text-xs text-slate-400">{project.completion_percentage || 0}%</span>
                        </div>
                      </td>
                      <td>
                        <Badge className={project.status === 'active' ? 'status-green' : 'status-yellow'}>
                          {project.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ExecutiveDashboard;
